import React, { useState, useEffect } from 'react'
import { useSearchStore } from '../store/searchStore'
import { FiSearch, FiFilter, FiChevronDown, FiChevronUp } from 'react-icons/fi'
import axios from '../config/axios'

function SearchForm() {
  const [length, setLength] = useState('')
  const [width, setWidth] = useState('')
  const [depth, setDepth] = useState('')
  const [showFilters, setShowFilters] = useState(true)
  const [selectedManufacturers, setSelectedManufacturers] = useState([])
  const [expandedManufacturers, setExpandedManufacturers] = useState([])
  const [seriesError, setSeriesError] = useState(false)
  const [processError, setProcessError] = useState(false)

  const {
    processes,
    selectedProcess,
    manufacturers,
    selectedSeries,
    loading,
    error,
    fetchSeries,
    setSelectedProcess,
    search,
    toggleSeries,
    setSelectedSeries,
    clearSearch,
    clearError
  } = useSearchStore()

  const [maxVolumes, setMaxVolumes] = useState({})
  const [noInjectionResults, setNoInjectionResults] = useState(false)

  // Fetch max volumes on load (used as fallback threshold)
  useEffect(() => {
    axios.get('/api/search/max-volumes')
      .then(r => setMaxVolumes(r.data.maxVolumes || {}))
      .catch(() => {})
  }, [])

  // Background check: when all 3 dims filled + Injection Mold selected, silently test if any results exist
  useEffect(() => {
    if (selectedProcess !== 'Injection Mold' || !length || !width || !depth || selectedSeries.length === 0) {
      setNoInjectionResults(false)
      return
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post('/api/search', {
          length: parseFloat(length),
          width: parseFloat(width),
          depth: parseFloat(depth),
          series: selectedSeries,
          process: 'Injection Mold',
          maxResults: 1
        })
        setNoInjectionResults((res.data.results?.length ?? 0) === 0)
      } catch {
        setNoInjectionResults(false)
      }
    }, 600)
    return () => clearTimeout(timer)
  }, [length, width, depth, selectedProcess, selectedSeries])

  // Volume-based fallback: fires immediately while background check is still pending
  const volume = (length && width && depth) ? parseFloat(length) * parseFloat(width) * parseFloat(depth) : 0
  const injectionMaxVolume = (maxVolumes['Injection Mold']?.volume || 330000) * 0.75
  const volumeWarning = selectedProcess === 'Injection Mold' && !!length && !!width && !!depth && volume > injectionMaxVolume

  const shouldShowVolumeWarning = noInjectionResults || volumeWarning

  // Switch to Rotomold and search immediately
  const switchAndSearchRotomold = async () => {
    setSelectedProcess('Rotomold')
    // Get all rotomold manufacturers and series
    const rotomoldProcess = processes.find(p => p.name === 'Rotomold')
    if (rotomoldProcess) {
      const allMfrs = rotomoldProcess.manufacturers.map(m => m.name)
      const allSeries = rotomoldProcess.manufacturers.flatMap(m => m.series)
      setSelectedManufacturers(allMfrs)
      setSelectedSeries(allSeries)
      // Search after state updates
      setTimeout(() => {
        search(length, width, depth)
      }, 0)
    }
  }

  useEffect(() => {
    fetchSeries()
  }, [fetchSeries])

  // Auto-select all manufacturers and series when process changes
  useEffect(() => {
    if (selectedProcess && manufacturers.length > 0) {
      setSelectedManufacturers(manufacturers.map(m => m.name))
      setSelectedSeries(manufacturers.flatMap(m => m.series))
    }
  }, [selectedProcess, manufacturers])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!length || !width || !depth) return
    if (!selectedProcess) {
      setProcessError(true)
      setShowFilters(true)
      return
    }
    if (selectedSeries.length === 0) {
      setSeriesError(true)
      setShowFilters(true)
      return
    }
    setProcessError(false)
    setSeriesError(false)
    setShowFilters(false)
    search(length, width, depth)
  }

  const handleClear = () => {
    setLength('')
    setWidth('')
    setDepth('')
    setProcessError(false)
    setSeriesError(false)
    setSelectedProcess(null)
    setSelectedManufacturers([])
    setSelectedSeries([])
    clearSearch()
  }

  const handleToggleManufacturer = (name) => {
    const mfr = manufacturers.find(m => m.name === name)
    const isSelected = selectedManufacturers.includes(name)
    if (isSelected) {
      setSelectedManufacturers(prev => prev.filter(m => m !== name))
      setSelectedSeries(selectedSeries.filter(s => !mfr.series.includes(s)))
      setExpandedManufacturers(prev => prev.filter(m => m !== name))
    } else {
      setSelectedManufacturers(prev => [...prev, name])
      setExpandedManufacturers(prev => [...prev, name])
      const toAdd = mfr.series.filter(s => !selectedSeries.includes(s))
      setSelectedSeries([...selectedSeries, ...toAdd])
      setSeriesError(false)
    }
  }

  const handleToggleExpand = (name) => {
    setExpandedManufacturers(prev =>
      prev.includes(name) ? prev.filter(m => m !== name) : [...prev, name]
    )
  }

  const handleToggleAllManufacturers = () => {
    if (selectedManufacturers.length === manufacturers.length) {
      setSelectedManufacturers([])
      setSelectedSeries([])
      setExpandedManufacturers([])
    } else {
      setSelectedManufacturers(manufacturers.map(m => m.name))
      setSelectedSeries(manufacturers.flatMap(m => m.series))
      setExpandedManufacturers([])
      setSeriesError(false)
    }
  }

  const handleToggleSeriesItem = (s) => {
    toggleSeries(s)
    setSeriesError(false)
  }

  const handleToggleAllSeriesForMfr = (mfr) => {
    const allSelected = mfr.series.every(s => selectedSeries.includes(s))
    if (allSelected) {
      setSelectedSeries(selectedSeries.filter(s => !mfr.series.includes(s)))
    } else {
      const toAdd = mfr.series.filter(s => !selectedSeries.includes(s))
      setSelectedSeries([...selectedSeries, ...toAdd])
    }
    setSeriesError(false)
  }

  return (
    <form onSubmit={handleSubmit} className="card space-y-4">
      {error && (
        <div className="p-3 bg-red-900/20 border border-red-800 rounded text-red-200 flex justify-between items-center text-sm">
          <span>{error}</span>
          <button type="button" onClick={() => clearError()} className="hover:underline ml-2">Dismiss</button>
        </div>
      )}


      {/* Compact dimension inputs + action buttons */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Length (cm)', value: length, set: setLength },
          { label: 'Width (cm)', value: width, set: setWidth },
          { label: 'Depth (cm)', value: depth, set: setDepth },
        ].map(({ label, value, set }) => (
          <div key={label} className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-accent-teal">{label}</label>
            <input
              type="number"
              step="any"
              min="0"
              inputMode="decimal"
              value={value}
              onChange={e => {
                const v = e.target.value
                if (v === '' || /^\d+(\.\d{0,2})?$/.test(v)) set(v)
              }}
              onKeyDown={e => {
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  e.preventDefault()
                  const current = parseFloat(value) || 0
                  const next = e.key === 'ArrowUp' ? current + 1 : Math.max(0, current - 1)
                  set(String(next))
                }
              }}
              placeholder="0.00"
              required
              className="w-full px-2 py-1.5 text-sm bg-bg-dark border border-border-dark rounded focus:outline-none focus:border-accent-teal text-white"
            />
          </div>
        ))}
      </div>

      {/* Volume warning for large dimensions */}
      {shouldShowVolumeWarning && (
        <div className="p-3 bg-orange-900/30 border border-orange-600/50 rounded text-orange-200 text-sm space-y-2">
          <p className="flex items-start gap-2">
            <span className="text-lg">⚠️</span>
            <span>
              These dimensions ({length} × {width} × {depth} cm) are at the upper limit of the Injection Mold range and may return no results.
              <br />Consider searching our larger Rotomold cases for better options.
            </span>
          </p>
          <button
            type="button"
            onClick={switchAndSearchRotomold}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-orange-600 text-white font-semibold text-xs hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {loading ? 'Searching…' : 'Search Rotomold Cases'}
          </button>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={loading || !length || !width || !depth || !selectedProcess || selectedSeries.length === 0}
          className="flex items-center gap-1.5 px-4 py-1.5 text-sm btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiSearch size={15} />
          {loading ? 'Searching…' : 'Find Cases'}
        </button>
        <button
          type="button"
          onClick={handleClear}
          className="px-3 py-1.5 text-sm border border-border-dark text-gray-400 hover:text-white hover:border-gray-400 rounded transition"
        >
          Clear
        </button>
      </div>

      <p className="text-xs font-semibold text-red-500">All dimensions shown are internal</p>

      {/* Filter toggle */}
      <div className="space-y-1">
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-accent-teal hover:text-cyan-400 transition text-sm font-semibold"
        >
          <FiFilter size={15} />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
          {selectedSeries.length > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-accent-teal/20 rounded text-xs">
              {selectedSeries.length} series selected
            </span>
          )}
        </button>
        {seriesError && (
          <p className="text-red-400 text-xs">Please select at least one case series before searching.</p>
        )}
      </div>

      {/* Multi-step filter */}
      {showFilters && (
        <div className="border-t border-border-dark pt-4 space-y-5">

          {/* Step 1 — Select Process */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold text-white">Step 1 — Select Process</h3>
            </div>
            {processError && (
              <p className="text-red-400 text-xs mb-2">Please select a process before searching.</p>
            )}
            <div className="flex flex-wrap gap-2">
              {processes.map(p => {
                const active = selectedProcess === p.name
                return (
                  <button
                    key={p.name}
                    type="button"
                    onClick={() => {
                      setSelectedProcess(p.name)
                      setProcessError(false)
                    }}
                    className={`px-3 py-1.5 rounded border text-sm transition hover:border-accent-teal/60 ${active ? 'border-accent-teal bg-accent-teal/10 text-accent-teal' : 'border-border-dark text-gray-400'}`}
                  >
                    {p.name}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Step 2 — Manufacturers (hidden when only one or no process selected) */}
          {selectedProcess && manufacturers.length > 1 && (
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold text-white">Step 2 — Select Manufacturers</h3>
              <button
                type="button"
                onClick={handleToggleAllManufacturers}
                className="text-xs px-2.5 py-1 rounded bg-accent-teal/20 hover:bg-accent-teal/30 text-accent-teal transition"
              >
                {selectedManufacturers.length === manufacturers.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {manufacturers.map(m => {
                const active = selectedManufacturers.includes(m.name)
                return (
                  <label
                    key={m.name}
                    className={`flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded border text-sm transition hover:border-accent-teal/60 ${active ? 'border-accent-teal bg-accent-teal/10 text-accent-teal' : 'border-border-dark text-gray-400'}`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => handleToggleManufacturer(m.name)}
                      className="w-3.5 h-3.5 accent-accent-teal"
                    />
                    {m.name}
                  </label>
                )
              })}
            </div>
          </div>
          )}

          {/* Step 3 — Series per selected manufacturer */}
          {selectedProcess && (manufacturers.length === 1 || selectedManufacturers.length > 0) && (
            <div>
              <h3 className="text-sm font-semibold text-white mb-3">{manufacturers.length > 1 ? 'Step 3 — Select Series' : 'Step 2 — Select Series'}</h3>
              <div className="space-y-2">
                {manufacturers
                  .filter(m => selectedManufacturers.includes(m.name))
                  .map(m => {
                    const isExpanded = expandedManufacturers.includes(m.name)
                    const selectedCount = m.series.filter(s => selectedSeries.includes(s)).length
                    const allChecked = selectedCount === m.series.length
                    const someChecked = selectedCount > 0 && !allChecked
                    return (
                      <div key={m.name} className="border border-border-dark rounded overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-bg-dark">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={allChecked}
                              ref={el => { if (el) el.indeterminate = someChecked }}
                              onChange={() => handleToggleAllSeriesForMfr(m)}
                              className="w-3.5 h-3.5 accent-accent-teal"
                            />
                            <span className="text-sm font-semibold text-accent-teal">{m.name}</span>
                            <span className="text-xs text-gray-500">{selectedCount}/{m.series.length}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleToggleExpand(m.name)}
                            className="text-gray-400 hover:text-white transition"
                          >
                            {isExpanded ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
                          </button>
                        </div>
                        {isExpanded && (
                          <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-3 border-t border-border-dark bg-bg-card/50">
                            {m.series.map(s => (
                              <label key={s} className="flex items-center gap-2 cursor-pointer hover:text-accent-teal transition min-w-0">
                                <input
                                  type="checkbox"
                                  checked={selectedSeries.includes(s)}
                                  onChange={() => handleToggleSeriesItem(s)}
                                  className="w-3.5 h-3.5 accent-accent-teal flex-shrink-0"
                                />
                                <span className="text-xs truncate">{s}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
              </div>
            </div>
          )}
        </div>
      )}
    </form>
  )
}

export default SearchForm
